import { forwardRef, useEffect, useImperativeHandle, useState } from 'react';
import { SearchIndex, init } from 'emoji-mart';
import data from '@emoji-mart/data';

let initialized = false;
async function ensureInit() {
  if (initialized) return;
  await init({ data });
  initialized = true;
}

type EmojiHit = { id: string; native: string; shortcodes: string };

type Props = {
  items: EmojiHit[];
  command: (e: { unicode: string; shortcode: string }) => void;
};

export const EmojiPopup = forwardRef<{ onKeyDown: (e: { event: KeyboardEvent }) => boolean }, Props>(
  ({ items, command }, ref) => {
    const [selectedIndex, setSelectedIndex] = useState(0);

    useEffect(() => setSelectedIndex(0), [items]);

    useImperativeHandle(ref, () => ({
      onKeyDown({ event }) {
        if (event.key === 'ArrowUp') { setSelectedIndex((i) => (i + items.length - 1) % items.length); return true; }
        if (event.key === 'ArrowDown') { setSelectedIndex((i) => (i + 1) % items.length); return true; }
        if (event.key === 'Enter') {
          const it = items[selectedIndex];
          if (it) command({ unicode: it.native, shortcode: `:${it.id}:` });
          return true;
        }
        return false;
      },
    }));

    if (items.length === 0) return null;

    return (
      <div className="z-50 min-w-[200px] rounded-lg border border-border bg-popover shadow-lg p-1">
        {items.slice(0, 8).map((it, i) => (
          <button
            key={it.id}
            type="button"
            onClick={() => command({ unicode: it.native, shortcode: `:${it.id}:` })}
            className={`flex w-full items-center gap-2 rounded px-2 py-1 text-left text-sm ${i === selectedIndex ? 'bg-accent' : ''}`}
          >
            <span className="text-lg">{it.native}</span>
            <span className="text-muted-foreground">:{it.id}:</span>
          </button>
        ))}
      </div>
    );
  },
);

EmojiPopup.displayName = 'EmojiPopup';

export async function searchEmoji(query: string): Promise<EmojiHit[]> {
  await ensureInit();
  const results = await SearchIndex.search(query);
  return (results ?? []).map((r: { id: string; skins: { native: string }[] }) => ({
    id: r.id,
    native: r.skins[0]?.native ?? '',
    shortcodes: `:${r.id}:`,
  }));
}
